import { render } from 'preact'
import ChatWidget from './components/ChatWidget'
import type { ChatWidgetApi } from './components/ChatWidget'
import type { ChatConfig } from './types/chat'
import { isPathAllowed } from './utils/pathMatcher'
import './styles/global.css'

declare global {
  interface Window {
    ChatWidget: {
      init: (config: ChatConfig) => void
      destroy: () => void
      /** Withdraw a previously given privacy consent and re-arm the gate (DSGVO Art. 7(3)). */
      revokeConsent: () => void
    }
  }
}

let container: HTMLElement | null = null
let cleanupNavListeners: (() => void) | null = null
let widgetApi: ChatWidgetApi | null = null

function applyVisibility(config: ChatConfig) {
  if (!container) return
  const { allowedPaths } = config
  if (!allowedPaths || allowedPaths.length === 0) {
    container.style.display = ''
    return
  }
  container.style.display = isPathAllowed(allowedPaths, window.location.pathname) ? '' : 'none'
}

function setupNavListeners(config: ChatConfig): () => void {
  const handler = () => applyVisibility(config)

  // Patch history methods so SPA navigations (pushState/replaceState) are detected
  const origPush = history.pushState.bind(history)
  const origReplace = history.replaceState.bind(history)

  history.pushState = (...args: Parameters<typeof history.pushState>) => {
    origPush(...args)
    handler()
  }
  history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
    origReplace(...args)
    handler()
  }

  window.addEventListener('popstate', handler)
  window.addEventListener('hashchange', handler)

  return () => {
    history.pushState = origPush
    history.replaceState = origReplace
    window.removeEventListener('popstate', handler)
    window.removeEventListener('hashchange', handler)
  }
}

window.ChatWidget = {
  init(config: ChatConfig) {
    if (container) {
      console.warn('[n8n-chatbot] ChatWidget is already initialized. Call destroy() first.')
      return
    }

    container = document.createElement('div')
    container.id = 'n8n-chatbot-root'
    document.body.appendChild(container)
    render(<ChatWidget config={config} onApiReady={(api) => { widgetApi = api }} />, container)

    applyVisibility(config)

    if (config.allowedPaths && config.allowedPaths.length > 0) {
      cleanupNavListeners = setupNavListeners(config)
    }
  },

  destroy() {
    if (!container) return
    cleanupNavListeners?.()
    cleanupNavListeners = null
    render(null, container)
    container.remove()
    container = null
    widgetApi = null
  },

  revokeConsent() {
    widgetApi?.revokeConsent()
  },
}
